import { createSlice } from "@reduxjs/toolkit";
import { backendConnector } from "../backendIntegration/lambdas";
import { createApiThunk, addAsyncCases } from "./sliceHelpers";

// A default/empty shape to maintain consistent state - simplified.
// The totals come from the server: a referral is worth nothing until the friend
// submits their first application, and nothing at all past the lifetime cap, so
// rows * 20 would promise swipes that were never granted.
const emptyReferralData = {
    totalReferrals: 0,
    earnedReferrals: 0,
    pendingReferrals: 0,
    cappedReferrals: 0,
    totalSwipesEarned: 0,
    rewardPerReferral: 20,
    maxRewardedReferrals: 10,
    // Cap progress is the server's own counter, not derived from the rows: legacy
    // referrals were paid at signup and never counted against the cap.
    rewardedReferrals: 0,
    hasReachedReferralCap: false,
    referrals: [],
};

export const fetchReferralData = createApiThunk(
    "referral/fetchReferralData",
    async () => {
        const response = await backendConnector.getReferralData();
        // This will be action.payload
        return response.referralData || emptyReferralData;
    },
    { errorMessage: "An unknown error occurred" }
);


export const referralSlice = createSlice({
    name: "referralData",
    initialState: {
        data: emptyReferralData,
        loading: false,
        loaded: false,
        error: null
    },
    reducers: {
        clearReferralData: (state) => {
            state.data = emptyReferralData;
            state.loading = false;
            state.loaded = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        addAsyncCases(builder, fetchReferralData, {
            loadingKey: "loading",
            loadedKey: "loaded",
            onFulfilled: (state, action) => {
                // action.payload is now the referralData object: { totalReferrals: X, referrals: [...] }
                const payload = action.payload || {};

                // `??`, not destructuring defaults: those only cover `undefined`, so a
                // JSON null would fall through and a paused programme (0) would render
                // as the old promise of 20.
                state.data = {
                    totalReferrals: payload.totalReferrals ?? 0,
                    earnedReferrals: payload.earnedReferrals ?? 0,
                    pendingReferrals: payload.pendingReferrals ?? 0,
                    cappedReferrals: payload.cappedReferrals ?? 0,
                    totalSwipesEarned: payload.totalSwipesEarned ?? 0,
                    rewardPerReferral:
                        payload.rewardPerReferral ?? emptyReferralData.rewardPerReferral,
                    maxRewardedReferrals:
                        payload.maxRewardedReferrals ?? emptyReferralData.maxRewardedReferrals,
                    rewardedReferrals: payload.rewardedReferrals ?? 0,
                    hasReachedReferralCap: Boolean(payload.hasReachedReferralCap),
                    referrals: payload.referrals ?? [],
                };
            },
            onRejected: (state, action) => {
                state.error = typeof action.payload === 'string' ? action.payload : "Failed to fetch referral data.";
            },
        });
    },

});

export const { clearReferralData } = referralSlice.actions;
export default referralSlice.reducer;